import React from "react";
import styled from "styled-components";
import { colors2, getBreakPoint } from "oolib";
import { UI_CAPTION_DF, UI_TAG_DF, UI_TITLE } from "oolib/dist/v2/components/Typo2";
import { SentimentBar } from "@/_self_managed/SM_Dashboard/LensesV2/comps/SentimentBar";

/**
 * One zone as a small card: the zone's name in its colour (the tag face),
 * a count, a caption beside it, and optionally a share bar under them.
 * The zones grid draws it with the share ("17% of obs" + bar, the Fires
 * tint behind); the Priorities zone bar draws it as a toggle with the
 * zone's reading ("steady & severe") and no bar or tint.
 *
 *   zone     { label, text, hint, field }  — from resolveZones
 *   count    number
 *   caption  string beside the count
 *   share    0–100 → the bar; omit for no bar
 *   tint     paint the zone's field colour behind the card
 *   selected / onClick  — a toggle; the card becomes a button
 */
const Shell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 12px 14px;
  border-radius: 10px;
  @media only screen and (max-width: ${getBreakPoint("md") - 1}px) {
    padding: 12px 8px;
  }
  border: 1px solid ${({ $selected }) => ($selected ? colors2.grey40 : colors2.grey10)};
  background: ${({ $bg, $selected }) => ($selected ? colors2.grey5 : $bg || colors2.white)};
  min-width: 0;
  text-align: left;
  ${({ $clickable, $selected }) =>
    $clickable
      ? `cursor: pointer; transition: background 0.12s ease, border-color 0.12s ease;
         &:hover { background: ${$selected ? colors2.grey5 : colors2.grey2}; }`
      : ""}
`;
// The count and its caption: stacked on a phone, where the card is too narrow
// for both on one line — tight, the caption a step smaller (UI_CAPTION's
// desktop size); side by side at their own sizes from md up.
const Figure = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0;
  margin-bottom: ${({ $bar }) => ($bar ? "-4px" : 0)};

  @media only screen and (max-width: ${getBreakPoint("md") - 1}px) {
    & > :last-child {
      font-size: 12px;
      line-height: 16px;
    }
  }

  @media only screen and (min-width: ${getBreakPoint("md")}px) {
    flex-direction: row;
    align-items: baseline;
    gap: 8px;
  }
`;
const Track = styled.div`
  width: 100%;
  height: 6px;
  border-radius: 3px;
  background: ${colors2.grey5};
  overflow: hidden;
`;

export const ZoneCard = ({ zone, count, caption, share, tint = false, selected = false, onClick }) => {
  const bar = typeof share === "number";
  return (
    <Shell
      as={onClick ? "button" : "div"}
      type={onClick ? "button" : undefined}
      aria-pressed={onClick ? selected : undefined}
      $clickable={!!onClick}
      $selected={selected}
      $bg={tint && zone.field !== "transparent" ? zone.field : undefined}
      title={zone.hint}
      onClick={onClick}
    >
      <UI_TAG_DF style={{ color: zone.text }}>{zone.label}</UI_TAG_DF>
      <Figure $bar={bar}>
        <UI_TITLE style={{ color: colors2.grey80 }}>{String(count)}</UI_TITLE>
        <UI_CAPTION_DF style={{ color: colors2.grey40, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{caption}</UI_CAPTION_DF>
      </Figure>
      {bar && (
        <Track>
          <div style={{ width: `${share}%` }}>
            <SentimentBar segments={[{ key: "share", title: zone.label, value: 100, color: zone.text }]} height={6} roundedEnds />
          </div>
        </Track>
      )}
    </Shell>
  );
};
